import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Container, Button, Alert, Form, Spinner } from 'react-bootstrap';
import { FaSlack } from 'react-icons/fa';
import axios from '../utils/axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// This page provides a transcript viewer and chat interface for meeting transcripts
const TranscriptViewer = () => {
  const [transcript, setTranscript] = useState('');
  const [summary, setSummary] = useState('');
  const [chatInput, setChatInput] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [meetings, setMeetings] = useState([]);
  const [selectedMeetingId, setSelectedMeetingId] = useState(null);
  const [slackSuccess, setSlackSuccess] = useState(false);
  const chatContainerRef = useRef(null);

  // Get token helper
  const getToken = () => {
    return localStorage.getItem('token') || localStorage.getItem('authToken');
  };

  // Function to share chat response to Slack
  const handleShareChatToSlack = async (messageText, question) => {
    const token = getToken();
    if (!token) {
      setError('Please log in to share to Slack');
      return;
    }
    
    // Find the selected meeting for title
    const selectedMeeting = meetings.find(m => m._id === selectedMeetingId);
    
    try {
      const response = await fetch(`${API_URL}/api/integrations/slack/post-message`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: `AI Insight: ${selectedMeeting?.title || 'Meeting Transcript'}`,
          question: question,
          content: messageText,
          meetingId: selectedMeetingId
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSlackSuccess(true);
        setTimeout(() => setSlackSuccess(false), 3000);
      } else {
        setError(data.message || 'Failed to share to Slack');
      }
    } catch (err) {
      console.error('Error sharing to Slack:', err);
      setError('Failed to share to Slack');
    }
  };

  // Function to share transcript to Slack
  const handleShareTranscriptToSlack = async () => {
    const token = getToken();
    if (!token) {
      setError('Please log in to share to Slack');
      return;
    }
    
    if (!transcript) {
      setError('No transcript to share');
      return;
    }
    
    const selectedMeeting = meetings.find(m => m._id === selectedMeetingId);
    
    try {
      const response = await fetch(`${API_URL}/api/integrations/slack/post-message`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: `📝 Transcript: ${selectedMeeting?.title || 'Meeting'}`,
          content: transcript.length > 2800 ? transcript.substring(0, 2800) + '\n\n... [truncated]' : transcript,
          meetingId: selectedMeetingId
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSlackSuccess(true);
        setTimeout(() => setSlackSuccess(false), 3000);
      } else {
        setError(data.message || 'Failed to share to Slack');
      }
    } catch (err) {
      console.error('Error sharing to Slack:', err);
      setError('Failed to share to Slack');
    }
  };

  // Function to share summary to Slack
  const handleShareSummaryToSlack = async () => {
    const token = getToken();
    if (!token) {
      setError('Please log in to share to Slack');
      return;
    }
    
    if (!summary) {
      setError('No summary to share');
      return;
    }
    
    const selectedMeeting = meetings.find(m => m._id === selectedMeetingId);
    
    try {
      const response = await fetch(`${API_URL}/api/integrations/slack/post-message`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: `📋 Summary: ${selectedMeeting?.title || 'Meeting'}`,
          content: summary,
          meetingId: selectedMeetingId
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSlackSuccess(true);
        setTimeout(() => setSlackSuccess(false), 3000);
      } else {
        setError(data.message || 'Failed to share to Slack');
      }
    } catch (err) {
      console.error('Error sharing to Slack:', err);
      setError('Failed to share to Slack');
    }
  };

  // When chat history updates, scroll to bottom
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatHistory]);

  // Function to fetch meetings
  const fetchMeetings = useCallback(async () => {
    try {
      console.log('Fetching meetings...');
      // Get userId from localStorage if available
      let userId = null;
      try {
        const user = JSON.parse(localStorage.getItem('user'));
        userId = user?.id;
        console.log('User ID for fetching meetings:', userId);
      } catch (e) {
        console.error('Error getting user from localStorage:', e);
      }

      // Include userId as a query parameter if available
      const response = await axios.get(userId ? `/api/meetings?userId=${userId}` : '/api/meetings');
      
      if (response.data && response.data.meetings) {
        const sortedMeetings = [...response.data.meetings].sort((a, b) => 
          new Date(b.createdAt) - new Date(a.createdAt)
        );
        console.log('Fetched meetings:', sortedMeetings.length);
        setMeetings(sortedMeetings);
        
        // If there are meetings and none is selected, select the most recent one
        if (sortedMeetings.length > 0 && !selectedMeetingId) {
          selectMeeting(sortedMeetings[0]._id);
        }
      }
    } catch (err) {
      console.error('Failed to fetch meetings:', err);
    }
  }, [selectedMeetingId]);

  // Fetch meetings on component mount
  useEffect(() => {
    fetchMeetings();
  }, [fetchMeetings]);

  // Note: Recording functions removed as this is now a transcript viewer only

  // Handle chat/QA with transcript
  // New function to fetch meeting data with transcript for chat
  const fetchMeetingDataForChat = async (meetingId) => {
    try {
      console.log('Fetching meeting data specifically for chat:', meetingId);
      
      // Get auth token from the response data stored in localStorage
      // The auth system stores the token separately from the user object
      let token = null;
      try {
        // First check if we have a direct token stored
        const storedToken = localStorage.getItem('token');
        if (storedToken) {
          token = storedToken;
          console.log('Using token directly from localStorage');
        } else {
          // Try to get from the auth response (this is how it's stored by the auth system)
          const authResponse = localStorage.getItem('authToken');
          if (authResponse) {
            token = authResponse;
            console.log('Using token from authToken in localStorage');
          } else {
            // As a fallback, check if token is stored with user object
            const user = JSON.parse(localStorage.getItem('user'));
            if (user && user.token) {
              token = user.token;
              console.log('Using token from user object in localStorage');
            }
          }
        }
      } catch (e) {
        console.error('Error retrieving authentication token:', e);
      }
      
      // For debugging - show if we found a token
      console.log('Authentication token available:', !!token);
      
      // Add a demo token for testing if none found (development only)
      // Remove this in production!
      if (!token) {
        console.log('DEVELOPMENT MODE: Using demo token for testing');
        token = 'demo-token-for-testing';
      }
      
      const response = await axios.get(`/api/desktop-chat/meeting/${meetingId}`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        }
      });
      
      if (response.data && response.data.success && response.data.meetingData) {
        console.log('Successfully fetched meeting data for chat');
        console.log('- Transcript length:', 
          response.data.meetingData.transcript ? response.data.meetingData.transcript.length : 0);
        return response.data.meetingData;
      } else {
        throw new Error('Invalid response format from server');
      }
    } catch (err) {
      console.error('Error fetching meeting data for chat:', err);
      throw err;
    }
  };

  const handleChat = async () => {
    if (!chatInput.trim()) return;
    setLoading(true);
    setError('');
    
    try {
      // Debug the current state
      console.log('Current state in handleChat:');
      console.log('- Selected Meeting ID:', selectedMeetingId);
      console.log('- Chat Input:', chatInput);
      
      if (!selectedMeetingId) {
        setError('Please select a meeting first');
        setLoading(false);
        return;
      }
      
      // Fetch fresh meeting data with transcript specifically for chat
      // This ensures we get the transcript even if it has select: false in the model
      let chatTranscript = transcript;
      let chatSummary = summary;
      
      try {
        const meetingData = await fetchMeetingDataForChat(selectedMeetingId);
        chatTranscript = meetingData.transcript || '';
        chatSummary = meetingData.summary || '';
        console.log('Using fresh transcript data for chat, length:', chatTranscript.length);
      } catch (err) {
        console.error('Failed to fetch fresh transcript, using existing data if available');
      }
      
      // Enhanced error message for missing transcript
      if (!chatTranscript || chatTranscript.trim() === '') {
        console.error('No transcript available for chat');
        setError('No transcript available. Please select a meeting with a transcript first.');
        setLoading(false);
        return;
      }
      
      // Get auth token using the same enhanced logic as fetchMeetingDataForChat
      let token = null;
      try {
        // First check if we have a direct token stored
        const storedToken = localStorage.getItem('token');
        if (storedToken) {
          token = storedToken;
          console.log('Using token directly from localStorage');
        } else {
          // Try to get from the auth response (this is how it's stored by the auth system)
          const authResponse = localStorage.getItem('authToken');
          if (authResponse) {
            token = authResponse;
            console.log('Using token from authToken in localStorage');
          } else {
            // As a fallback, check if token is stored with user object
            const user = JSON.parse(localStorage.getItem('user'));
            if (user && user.token) {
              token = user.token;
              console.log('Using token from user object in localStorage');
            }
          }
        }
      } catch (e) {
        console.error('Error retrieving authentication token:', e);
      }
      
      // For debugging - show if we found a token
      console.log('Authentication token available:', !!token);
      
      // Add a demo token for testing if none found (development only)
      if (!token) {
        console.log('DEVELOPMENT MODE: Using demo token for testing');
        token = 'demo-token-for-testing';
      }
      
      // Use the AI-powered endpoint for chat/QA with auth headers
      // Include both transcript and summary for better context
      const response = await axios.post('/api/desktop-chat', {
        transcript: chatTranscript,
        summary: chatSummary,  // Include summary along with transcript
        message: chatInput
      }, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        }
      });
      
      setChatHistory([...chatHistory, { sender: 'user', text: chatInput }, { sender: 'ai', text: response.data.reply }]);
      setChatInput('');
    } catch (err) {
      console.error('Chat error:', err);
      setError(err.response?.data?.message || 'Chat failed. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Select a meeting to view transcript and chat
  const selectMeeting = async (meetingId) => {
    try {
      console.log('Selecting meeting:', meetingId);
      setSelectedMeetingId(meetingId);
      setLoading(true);
      setChatHistory([]);
      const response = await axios.get(`/api/meetings/${meetingId}`);
      console.log('Meeting data received:', response.data);
      
      if (response.data && response.data.meeting) {
        const meetingTranscript = response.data.meeting.transcript || '';
        const meetingSummary = response.data.meeting.summary || '';
        
        console.log('Setting transcript, length:', meetingTranscript.length);
        console.log('Setting summary, length:', meetingSummary.length);
        
        setTranscript(meetingTranscript);
        setSummary(meetingSummary);
      } else {
        console.error('Meeting data format unexpected:', response.data);
        setError('Invalid meeting data format received');
      }
    } catch (err) {
      console.error('Failed to load meeting data:', err);
      setError('Failed to load meeting data: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container fluid className="px-0 d-flex flex-column transcript-page">
      {/* Chat-like interface with sidebar and main content */}
      <div className="d-flex flex-column flex-md-row flex-grow-1" style={{ overflow: 'hidden' }}>
        {/* Left sidebar - Recordings list */}
        <div className="transcript-sidebar border-end">
          <div className="p-3 border-bottom d-flex justify-content-between align-items-center">
            <h4 className="mb-0">Transcripts</h4>
            <Button 
              variant="outline-secondary" 
              size="sm" 
              onClick={fetchMeetings}
              title="Refresh transcripts list"
            >
              ↻
            </Button>
          </div>
          
          {/* Recordings list */}
          <div className="py-2">
            {meetings.length > 0 ? (
              meetings.map(meeting => (
                <div 
                  key={meeting._id} 
                  className={`d-flex p-3 border-bottom align-items-center ${selectedMeetingId === meeting._id ? 'transcript-item-active' : 'hover-light'}`} 
                  style={{ cursor: 'pointer' }}
                  onClick={() => selectMeeting(meeting._id)}
                >
                  <div className="flex-grow-1">
                    <div className="fw-bold text-truncate">
                      {meeting.title || new Date(meeting.createdAt).toLocaleString()}
                    </div>
                    <div className="text-muted small text-truncate">
                      {meeting.summary ? meeting.summary.substring(0, 60) + '...' : 'No summary available'}
                    </div>
                  </div>
                </div>
              ))
            ) : loading ? (
              <div className="text-center py-4">
                <Spinner animation="border" size="sm" className="me-2" />
                Loading recordings...
              </div>
            ) : (
              <div className="text-center py-4 text-muted">
                No transcripts available yet.
              </div>
            )}
          </div>
        </div>

        {/* Main chat area */}
        <div className="flex-grow-1 d-flex flex-column" style={{ overflow: 'hidden' }}>
          {selectedMeetingId ? (
            <>
              {/* Slack success message */}
              {slackSuccess && (
                <Alert variant="success" className="m-2 py-1 small">
                  <FaSlack className="me-1" /> Shared to Slack!
                </Alert>
              )}

              {/* Collapsible Transcript/Summary area - smaller on mobile */}
              <div className="transcript-summary-area border-bottom p-2">
                <details className="mb-2 expandable-section">
                  <summary className="d-flex justify-content-between align-items-center" style={{ cursor: 'pointer', listStyle: 'none' }}>
                    <div className="d-flex align-items-center">
                      <i className="bi bi-chevron-right expand-arrow me-2"></i>
                      <span className="fw-bold small">Transcript</span>
                    </div>
                    {transcript && (
                      <Button 
                        variant="outline-secondary" 
                        size="sm"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleShareTranscriptToSlack(); }}
                        className="d-flex align-items-center py-0 px-2"
                      >
                        <FaSlack size={12} />
                      </Button>
                    )}
                  </summary>
                  <div className="transcript-content-box border rounded p-2 mt-1" style={{ 
                    maxHeight: '100px', 
                    overflow: 'auto', 
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    whiteSpace: 'pre-wrap'
                  }}>
                    {transcript || <span className="text-muted">No transcript available</span>}
                  </div>
                </details>
                
                <details className="expandable-section">
                  <summary className="d-flex justify-content-between align-items-center" style={{ cursor: 'pointer', listStyle: 'none' }}>
                    <div className="d-flex align-items-center">
                      <i className="bi bi-chevron-right expand-arrow me-2"></i>
                      <span className="fw-bold small">Summary</span>
                    </div>
                    {summary && (
                      <Button 
                        variant="outline-secondary" 
                        size="sm"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleShareSummaryToSlack(); }}
                        className="d-flex align-items-center py-0 px-2"
                      >
                        <FaSlack size={12} />
                      </Button>
                    )}
                  </summary>
                  <div className="transcript-content-box border rounded p-2 mt-1 small" style={{ maxHeight: '80px', overflow: 'auto' }}>
                    {summary || <span className="text-muted">No summary available</span>}
                  </div>
                </details>
              </div>

              {/* Chat area - takes most of the space */}
              <div className="flex-grow-1 p-2 d-flex flex-column chat-area" style={{ overflow: 'hidden', minHeight: '200px' }}>
                <div className="mb-2 d-flex gap-1 flex-wrap quick-actions">
                  <Button variant="outline-primary" size="sm" className="py-1 px-2 small" disabled={loading} onClick={() => { setChatInput('Summarize this transcript'); handleChat(); }}>
                    Summarize
                  </Button>
                  <Button variant="outline-success" size="sm" className="py-1 px-2 small" disabled={loading} onClick={() => { setChatInput('Extract all to-do items from this transcript'); handleChat(); }}>
                    To-Do
                  </Button>
                  <Button variant="outline-warning" size="sm" className="py-1 px-2 small" disabled={loading} onClick={() => { setChatInput('Draft an email based on this transcript'); handleChat(); }}>
                    Email
                  </Button>
                </div>
                
                <div ref={chatContainerRef} className="flex-grow-1 border rounded chat-container-box" style={{ overflowY: 'auto', padding: '8px', minHeight: '150px' }}>
                  {chatHistory.length === 0 ? (
                    <div className="text-center text-muted small py-4">
                      Ask anything about this meeting...
                    </div>
                  ) : (
                    chatHistory.map((msg, idx) => (
                      <div key={idx} className={`d-flex ${msg.sender === 'user' ? 'justify-content-end' : 'justify-content-start'} mb-3`}>
                        <div className={`p-3 rounded-3 ${msg.sender === 'user' ? 'bg-primary text-white' : 'chat-message-ai'}`} style={{ maxWidth: '75%', whiteSpace: 'pre-wrap' }}>
                          {msg.text}
                          {msg.sender === 'ai' && (
                            <div className="mt-2 pt-2 border-top">
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => {
                                  const prevMsg = chatHistory[idx - 1];
                                  const question = prevMsg?.sender === 'user' ? prevMsg.text : '';
                                  handleShareChatToSlack(msg.text, question);
                                }}
                                className="d-flex align-items-center"
                              >
                                <FaSlack className="me-1" /> Share to Slack
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
              
              {/* Chat input area */}
              <div className="border-top p-3 d-flex">
                <Form.Control
                  className="me-2"
                  value={chatInput}
                  onChange={e => setChatInput(e.target.value)}
                  placeholder="Ask about this transcript..."
                  disabled={loading}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleChat(); } }}
                />
                <Button variant="primary" onClick={handleChat} disabled={loading || !chatInput.trim()}>
                  Send
                </Button>
              </div>
            </>
          ) : (
            <div className="d-flex flex-column justify-content-center align-items-center h-100 text-center p-4">
              <div className="mb-3 text-muted">
                Select a transcript from the sidebar to view details
              </div>
              
              {/* Debug panel to check transcript data */}
              <div className="mt-4 p-3 border rounded debug-panel" style={{width: '100%', maxWidth: '600px', textAlign: 'left'}}>
                <h5>Debug Panel</h5>
                <p><strong>Selected Meeting ID:</strong> {selectedMeetingId || 'None'}</p>
                <p><strong>Transcript Available:</strong> {transcript ? 'Yes' : 'No'}</p>
                <p><strong>Transcript Length:</strong> {transcript ? transcript.length : 0} characters</p>
                <p><strong>Summary Available:</strong> {summary ? 'Yes' : 'No'}</p>
                <p><strong>Summary Length:</strong> {summary ? summary.length : 0} characters</p>
                
                <div className="mt-3">
                  <Button variant="secondary" size="sm" onClick={() => fetchMeetings()}>
                    Refresh Meetings List
                  </Button>{' '}
                  {window.lastMeetingData && (
                    <Button 
                      variant="info" 
                      size="sm" 
                      onClick={() => {
                        console.log('Loading from last meeting data cache', window.lastMeetingData);
                        setTranscript(window.lastMeetingData.transcript);
                        setSummary(window.lastMeetingData.summary);
                        setSelectedMeetingId(window.lastMeetingData.id);
                      }}
                    >
                      Load Last Transcript
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Status area at bottom */}
      <div className="p-3 border-top" style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {error && <Alert variant="danger" className="w-100 mb-3">{error}</Alert>}
        {loading && <Spinner animation="border" />}
      </div>
    </Container>
  );
};

export default TranscriptViewer;
